// Onboarding data schemas. Persisted server-side as a single pref-storage key
// ("onboarding.state") so it survives localStorage.clear() (the Settings
// "reset app" action) and follows the user across devices.

export type AssetClassId =
  | "equities"
  | "fx"
  | "fixed_income"
  | "energy"
  | "metals"
  | "crypto"
  | "macro";

export type OnboardingStep = "profile" | "concierge" | "watchlists" | "dashboard";

export type ChecklistTaskId =
  | "popout_widget"
  | "create_watchlist"
  | "apply_filter"
  | "set_notification"
  | "listen_squawk"
  | "download_app";

export type OnboardingAnswers = {
  assetClasses: AssetClassId[];
  /**
   * Free-text "what do you trade / want to monitor". Kept verbatim — this is
   * the LLM concierge's input; never parsed client-side.
   */
  freeText?: string;
  /**
   * Tickers the user actually holds, typed or uploaded on step 1.
   *
   * Unlike `freeText` this is unambiguous, so it is treated as the strongest
   * signal we have: it becomes a real watchlist on both routes, and the
   * concierge is told about it rather than left to infer names from prose.
   */
  tickers?: string[];
};

export type OnboardingState = {
  version: 1;
  /** Written the moment the gate first fires — makes triggering idempotent. */
  startedAt: string;
  /** Set when the user finishes the final step. */
  completedAt?: string;
  /** Set when the user clicks "Skip for now". */
  dismissedAt?: string;
  /** Resume point across sessions. */
  lastStep?: OnboardingStep;
  answers?: OnboardingAnswers;
  /** NEWSQUAWK portfolio IDs chosen in the watchlist step. */
  selectedWatchlistIds?: number[];
  /** Which path built their dashboard: a template id or "concierge". */
  appliedTemplateId?: string;
  checklist: {
    dismissedAt?: string;
    /** taskId -> ISO completion timestamp. Absent = not done. */
    tasks: Partial<Record<ChecklistTaskId, string>>;
  };
};

export const ONBOARDING_STATE_KEY = "onboarding.state";

export function createInitialOnboardingState(): OnboardingState {
  return {
    version: 1,
    startedAt: new Date().toISOString(),
    lastStep: "profile",
    checklist: { tasks: {} },
  };
}
